// Presets — list of named RunConfig templates. Operators land here
// to browse, duplicate, set-default, delete, or open a preset for
// editing. The Start-run flow lives on the Runs page (presets are
// templates, not actions).
//
// SM-525/SM-530 — every preset has exactly one trading mode, shown as a
// badge on its row and filterable with All/Cash/Game tabs. The filter
// is URL-persisted and applied server-side; "All" is a view, never a
// mode a preset can have.
//
// To operate both channels on one account an operator starts TWO runs
// from two single-mode presets. That is the only supported shape —
// there is no mixed-mode preset and no cross-mode start.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { invalidateAffected } from "@/lib/routeRefresh";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { useI18n } from "@/i18n/I18nProvider";
import { effectiveTradingMode } from "@/lib/tradingMode";
import { ModeFilterTabs, TradingModeBadge, type ModeFilter } from "@/components/mode/TradingModeBadge";
import type { TradingMode } from "@/lib/types";
import { strategyNameLabel } from "@/i18n/strategyCopy";
import { humanizeMMError } from "@/lib/errorMessages";
import { useStartRunPreflight, type StartRequest } from "@/components/runs/StartRunPreflight";
import {
  fetchAutoStartResults,
  fetchPresets,
  keys,
  deletePreset,
  duplicatePreset,
  setPresetAutoStart,
  startRun,
} from "@/lib/queries";
import { ModeBadge } from "@/components/runs/RunStateTabs";
import { effectiveTradingMode as presetTradingMode } from "@/lib/tradingMode";
import type { MMAutoStartOutcome, MMAutoStartResult } from "@/lib/types";
import type { TranslationKey } from "@/i18n/en";

interface PresetsSearch {
  // Optional and omitted when unset — see the same note on the Runs
  // route: it keeps `/presets` clean and keeps existing `<Link
  // to="/presets">` call sites valid.
  mode?: TradingMode;
}

export const Route = createFileRoute("/_app/presets/")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.presets),
  validateSearch: (search: Record<string, unknown>): PresetsSearch => {
    const mode = search.mode;
    return mode === "CASH" || mode === "GAME" ? { mode } : {};
  },
  component: PresetsPage,
});

function PresetsPage() {
  const { t, formatDateTime } = useI18n();
  const navigate = useNavigate();
  const routeNavigate = Route.useNavigate();
  const qc = useQueryClient();
  const { mode = "" } = Route.useSearch();
  const [page, setPage] = useState(0);
  const limit = 50;

  function setMode(next: ModeFilter) {
    // Page 1 of Cash is not page 1 of Game — keeping the offset across a
    // filter change lands the operator inside a different result set.
    setPage(0);
    routeNavigate({ search: () => (next ? { mode: next } : {}), replace: true });
  }

  const q = useQuery({
    queryKey: keys.presets(limit, page * limit, mode || undefined),
    queryFn: () => fetchPresets(limit, page * limit, mode || undefined),
  });

  const invalidateAll = () => invalidateAffected(qc, "presets");
  const dupMut = useMutation({ mutationFn: duplicatePreset, onSuccess: invalidateAll });
  const delMut = useMutation({ mutationFn: deletePreset, onSuccess: invalidateAll });
  // Auto-start is per-preset and ADDITIVE: arming one leaves the
  // others armed. The old set-default call disarmed every other
  // preset, which a two-desk deployment cannot express.
  const autoStartMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => setPresetAutoStart(id, enabled),
    onSuccess: invalidateAll,
  });
  // The restart report polls: a run it points at is created PENDING and
  // bootstraps asynchronously, so the row's live status must follow it
  // to RUNNING or FAILED without a reload. 3s while any linked run is
  // still active (the run-detail idiom), a slow tick otherwise so a
  // page left open stays correct.
  const bootQ = useQuery({
    queryKey: keys.autoStartResults(),
    queryFn: fetchAutoStartResults,
    refetchInterval: (q) =>
      q.state.data?.results.some((r) => r.run?.is_active) ? 3_000 : 15_000,
  });
  const startMut = useMutation({
    mutationFn: (req: StartRequest) =>
      startRun({ preset_id: req.presetId, expected_funding_plan: req.expectedFundingPlan }),
    onSuccess: (run) => {
      // The new run belongs to the Runs list and to this preset's
      // history, neither of which is on screen yet (SM-563 AC5).
      invalidateAffected(qc, "runs");
      navigate({ to: "/runs/$id", params: { id: run.run_id } });
    },
  });
  // Every start goes through the shared preflight (SM-526 / SM-531): the
  // GAME gate as an in-product banner, then the server's per-currency
  // funding review, whose plan fingerprint the start echoes back.
  const preflight = useStartRunPreflight((req) => startMut.mutate(req));

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="page-title">{t("presets.title")}</h1>
          <p className="page-subtitle">
            {t("presets.subtitle")}
          </p>
        </div>
        <Link to="/presets/new">
          <Button>{t("presets.new")}</Button>
        </Link>
      </header>
      <DataFreshness query={q} relatedQueries={[bootQ]} />

      <ModeFilterTabs value={mode} onChange={setMode} />

      <ErrorBanner error={startMut.error} fallbackTitle={t("presets.startError")} />
      <ErrorBanner error={autoStartMut.error} fallbackTitle={t("autoStart.error")} />
      {preflight.blockedBanner}
      {preflight.dialog}

      {/* Per-preset boot results. Each preset reports its own outcome
          and its own reason, because an aggregate status could not say
          WHICH preset conflicted — and the whole point of independent
          auto-start is that one failure does not stop the others.

          TWO status columns, deliberately (SM-547). "Restart result" is
          what boot DID — created a run, skipped, failed to dispatch —
          and is immutable. "Run status" is the linked run's LIVE state,
          read on every poll. The first cut showed only the former, and
          "Started" beside a run that had failed at bootstrap read as a
          running bot. */}
      {(bootQ.data?.results.length ?? 0) > 0 && (
        <Card className="section-card">
          <div className="section-head">
            <h3 className="section-title">{t("autoStart.resultsTitle")}</h3>
            {bootQ.data?.booted_at && (
              <span className="text-xs t-mute">
                {t("autoStart.bootedAt", { time: formatDateTime(bootQ.data.booted_at) })}
              </span>
            )}
          </div>
          <div className="p-[18px]">
            <table className="tbl rounded-none border-0">
              <thead>
                <tr>
                  <th>{t("presets.name")}</th>
                  <th title={t("autoStart.resultColumnHint")}>{t("autoStart.resultColumn")}</th>
                  <th>{t("runs.run")}</th>
                  <th>{t("autoStart.runStatusColumn")}</th>
                  <th>{t("runs.reason")}</th>
                </tr>
              </thead>
              <tbody>
                {(bootQ.data?.results ?? []).map((r) => (
                  <tr key={r.preset_id}>
                    <td className="text-sm text-t-1">{r.preset_name}</td>
                    <td>
                      <Badge tone={outcomeTone(r.outcome)} title={t("autoStart.resultColumnHint")}>
                        {t(OUTCOME_KEYS[r.outcome])}
                      </Badge>
                    </td>
                    <td>
                      {r.run_id ? (
                        // The FULL id: an operator quoting a failed run to
                        // a colleague must not have to open it to finish
                        // the identifier.
                        <Link
                          to="/runs/$id"
                          params={{ id: r.run_id }}
                          className="mono break-all text-xs t-emph hover:underline"
                        >
                          {r.run_id}
                        </Link>
                      ) : (
                        <span className="t-mute text-xs">—</span>
                      )}
                    </td>
                    <td>
                      <LinkedRunStatus result={r} />
                    </td>
                    <td className="text-xs t-mute">{r.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {q.isLoading && (
        <Card>
          <Skeleton lines={5} />
        </Card>
      )}

      {q.data && q.data.presets.length === 0 && (
        <Card>
          <div className="empty">
            {t("presets.emptyBefore")} {" "}
            <Link to="/presets/new" className="t-emph hover:underline">{t("presets.createOne")}</Link>
            {" "}{t("presets.emptyAfter")}
          </div>
        </Card>
      )}

      {q.data && q.data.presets.length > 0 && (
        <Card className="section-card">
          <div className="p-0">
            <table className="tbl rounded-none border-0">
              <thead>
                <tr>
                  <th>{t("presets.name")}</th>
                  <th>{t("presets.modeColumn")}</th>
                  <th>{t("presets.strategy")}</th>
                  <th>{t("presets.pools")}</th>
                  <th>{t("common.updated")}</th>
                  <th className="text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {q.data.presets.map((p) => (
                  <tr key={p.preset_id}>
                    <td>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to="/presets/$id" params={{ id: p.preset_id }} className="text-t-1 hover:t-emph">
                          {p.name}
                        </Link>
                        <ModeBadge mode={presetTradingMode(p.config)} />
                        {/* "Starts on restart", never "Default": the old
                            word implied exactly one preset, which is the
                            thing that changed. */}
                        {p.auto_start_enabled && (
                          <Badge tone="accent" title={t("autoStart.willStartHint")}>
                            {t("autoStart.willStart")}
                          </Badge>
                        )}
                      </div>
                      {p.description && <p className="mt-0.5 text-xs t-mute">{p.description}</p>}
                    </td>
                    {/* The badge is the row's currency warning: a Game
                        preset spends RED and pays GREEN, and Start on
                        the wrong row is a wrong-currency action. */}
                    <td><TradingModeBadge mode={p.trading_mode ?? effectiveTradingMode(p.config)} /></td>
                    <td className="text-xs">{strategyNameLabel(p.config.strategy.name, t)}</td>
                    <td className="text-xs">
                      {t("presets.poolSummary", {
                        bid: p.config.pools.bid_maker_count,
                        ask: p.config.pools.ask_maker_count,
                        taker: p.config.pools.taker_count,
                      })}
                    </td>
                    <td className="text-xs t-mute">{formatDateTime(p.updated_at)}</td>
                    <td>
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => void preflight.begin(p)}
                          loading={
                            preflight.pricingId === p.preset_id ||
                            (startMut.isPending && startMut.variables?.presetId === p.preset_id)
                          }
                        >
                          {t("presets.start")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => dupMut.mutate(p.preset_id)}
                          loading={dupMut.isPending && dupMut.variables === p.preset_id}
                        >
                          {t("presets.duplicate")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            autoStartMut.mutate({
                              id: p.preset_id,
                              enabled: !p.auto_start_enabled,
                            })
                          }
                          loading={
                            autoStartMut.isPending && autoStartMut.variables?.id === p.preset_id
                          }
                        >
                          {p.auto_start_enabled ? t("autoStart.disable") : t("autoStart.enable")}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => {
                            if (confirm(t("presets.deleteConfirm", { name: p.name }))) {
                              delMut.mutate(p.preset_id);
                            }
                          }}
                          loading={delMut.isPending && delMut.variables === p.preset_id}
                        >
                          {t("common.delete")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {q.data && q.data.page_info.has_more && (
        <div className="flex justify-center">
          <Button variant="ghost" onClick={() => setPage((p) => p + 1)}>{t("common.loadMore")}</Button>
        </div>
      )}
    </div>
  );
}

// Per-preset boot outcomes, mapped explicitly so a missing
// translation cannot ship as a raw enum on screen.
const OUTCOME_KEYS: Record<MMAutoStartOutcome, TranslationKey> = {
  STARTED: "autoStart.outcome.STARTED",
  RECOVERED: "autoStart.outcome.RECOVERED",
  SKIPPED: "autoStart.outcome.SKIPPED",
  FAILED: "autoStart.outcome.FAILED",
};

// STARTED is "neutral", not "yes": it records that a run was CREATED,
// and a green chip there is exactly what made a dispatched-then-failed
// run read as success (SM-547). The run's own status carries the colour.
// SKIPPED is "neutral" too: nothing is broken — the preset simply cannot
// run in the current arrangement, which calls for a configuration change
// rather than an investigation.
function outcomeTone(outcome: MMAutoStartOutcome): "yes" | "accent" | "neutral" | "no" {
  switch (outcome) {
    case "STARTED":
      return "neutral";
    case "RECOVERED":
      return "accent";
    case "SKIPPED":
      return "neutral";
    default:
      return "no";
  }
}

// The linked run's LIVE lifecycle state (SM-547). A FAILED run shows its
// leading code and the humanized reason inline, with the raw error on
// the title, so "Failed · MM_000214 · funding account …" sits right
// beside the immutable "Run created" it would otherwise contradict.
function LinkedRunStatus({ result }: { result: MMAutoStartResult }) {
  const { t } = useI18n();
  const run = result.run;
  if (!run) {
    return (
      <span className="t-mute text-xs">{result.run_id ? t("autoStart.runStateUnknown") : t("autoStart.noRun")}</span>
    );
  }
  const failure = run.status === "FAILED" && run.last_error ? humanizeMMError(run.last_error) : null;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusBadge status={run.status} />
        {run.error_code && (
          <span className="mono rounded bg-bg-2 px-1.5 py-0.5 text-2xs t-mute">{run.error_code}</span>
        )}
      </div>
      {failure && (
        <span className="max-w-96 text-xs t-mute" title={run.last_error}>
          {failure.title}
        </span>
      )}
    </div>
  );
}
